import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Spin } from 'antd';

/**
 * Progress Status Bar Component
 * Always visible at bottom - shows status based on processing state
 * Polls the status tracker service when processing
 */
const ProgressStatusBar = ({ isProcessing, agentName, siderCollapsed, sessionId, agentId }) => {
  // Local state for status data
  const [currentStatus, setCurrentStatus] = useState('Ready');
  const [currentPercentage, setCurrentPercentage] = useState(null);
  const [currentIcon, setCurrentIcon] = useState(null);
  
  // Polling interval reference
  const pollIntervalRef = useRef(null);
  
  // Status tracker service configuration
  const STATUS_TRACKER_URL = process.env.REACT_APP_STATUS_TRACKER_URL || 'http://localhost:8004';
  const POLL_INTERVAL = 1000; // Poll every second

  // Calculate left margin based on sider state
  const leftMargin = siderCollapsed ? '100px' : '240px';

  // Get status icon based on type
  const getStatusIcon = (iconType) => {
    switch(iconType) {
      case 'success': return '✅';
      case 'processing': return '⏳';
      case 'warning': return '⚠️';
      case 'pending': return '🟣'; // Purple dot for not yet processed
      default: return null;
    }
  };

  // Auto-detect icon from status text based on keywords
  const getAutoIcon = (status) => {
    const statusLower = status.toLowerCase();
    
    // Check for Success keywords
    if (statusLower.includes('success:') || 
        statusLower.includes('completed:') || 
        statusLower.includes('finished:') ||
        statusLower.includes('done:')) return 'success';
    
    // Check for Warning keywords
    if (statusLower.includes('warning:') || 
        statusLower.includes('warn:') ||
        statusLower.includes('caution:')) return 'warning';
    
    // Check for Processing keywords
    if (statusLower.includes('processing:') || 
        statusLower.includes('working:') || 
        statusLower.includes('running:') ||
        statusLower.includes('executing:')) return 'processing';
    
    // Check for Not Yet Processed keywords
    if (statusLower.includes('notyetprocessed:') || 
        statusLower.includes('notprocessed:') ||
        statusLower.includes('pending:') ||
        statusLower.includes('waiting:') ||
        statusLower.includes('queued:')) return 'pending';
    
    // Default fallback - if contains general processing words without colon
    if (statusLower.includes('process') || 
        statusLower.includes('working') || 
        statusLower.includes('loading')) return 'processing';
        
    return null;
  };

  // Fetch status from status tracker service
  const fetchStatus = useCallback(async () => {
    if (!sessionId) {
      return;
    }

    try {
      const response = await fetch(`${STATUS_TRACKER_URL}/get_status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: sessionId,
          agent_id: agentId || undefined
        })
      });

      if (response.ok) {
        const result = await response.json();
        const status = result.status || 'Agent working...';
        const percentage = result.percentage;
        const icon = result.icon || getAutoIcon(status); // Use provided icon or auto-detect
        
        setCurrentStatus(status);
        setCurrentPercentage(percentage);
        setCurrentIcon(icon);
      } else {
        setCurrentStatus('Agent working...');
        setCurrentPercentage(null);
        setCurrentIcon('processing');
      }
    } catch (error) {
      console.error('Error fetching status:', error);
      setCurrentStatus('Agent working...');
      setCurrentPercentage(null);
      setCurrentIcon('processing');
    }
  }, [sessionId, agentId, STATUS_TRACKER_URL]);

  // Start/stop polling based on isProcessing
  useEffect(() => {
    if (isProcessing && sessionId) {
      console.log('Starting status polling for session:', sessionId, 'agent:', agentId);
      
      // Fetch immediately
      fetchStatus();
      
      // Start polling interval
      pollIntervalRef.current = setInterval(fetchStatus, POLL_INTERVAL);
    } else {
      // Stop polling and reset to ready state
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
        console.log('Stopped status polling');
      }
      
      // Reset to ready state when not processing
      setCurrentStatus('Ready');
      setCurrentPercentage(null);
      setCurrentIcon(null);
    }

    // Cleanup on unmount or when isProcessing changes
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [isProcessing, sessionId, agentId, fetchStatus]);

  return (
    <div style={{
      position: 'fixed',
      bottom: 0,
      left: leftMargin,
      right: 0,
      height: '50px',
      backgroundColor: '#fcfcfcff',
      color: 'purple',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.15)',
      zIndex: 999,
      padding: '0 24px',
      transition: 'left 0.3s ease'
    }}>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '16px',
        maxWidth: '1400px',
        width: '100%',
        whiteSpace: 'nowrap'
      }}>
        {/* Static Label */}
        <div style={{
          fontWeight: '600',
          fontSize: '16px',
          opacity: 0.9,
          minWidth: '120px'
        }}>
          Agent Status:
        </div>

        {isProcessing ? (
          <>
            {/* Spinner when processing */}
            <Spin size="default" style={{ color: 'white' }} />
            
            {/* Display current status with icon */}
            <div style={{ 
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: '16px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center'
            }}>
              {currentIcon && (
                <span style={{ marginRight: '8px', fontSize: '16px' }}>
                  {getStatusIcon(currentIcon)}
                </span>
              )}
              <span>
                {currentStatus}
                {currentPercentage !== null && (
                  <span> • {currentPercentage}% complete</span>
                )}
              </span>
            </div>

            {/* Progress Bar (if percentage available) */}
            {currentPercentage !== null && (
              <div style={{ 
                width: '150px',
                height: '6px',
                backgroundColor: 'rgba(255, 255, 255, 0.3)',
                borderRadius: '3px',
                overflow: 'hidden',
                flexShrink: 0
              }}>
                <div style={{
                  width: `${currentPercentage}%`,
                  height: '100%',
                  backgroundColor: 'white',
                  transition: 'width 0.3s ease',
                  borderRadius: '3px'
                }} />
              </div>
            )}
          </>
        ) : (
          // Idle state - show "Ready"
          <div style={{ 
            fontSize: '16px',
            fontWeight: '500',
            opacity: 0.9
          }}>
            {currentStatus}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProgressStatusBar;